import { useCallback, useEffect, useState } from "react";

import { HardHat } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";
import ContractorDialog from "../ContractorCosting/component/ContractorDialog";
import ContractorTable from "../ContractorCosting/component/ContractorTable";

const Contractors = () => {
  const { user } = useAuth();
  const api = useApi();

  // Modals state
  const [isContractorDialogOpen, setIsContractorDialogOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);

  // Pagination for Contractors
  const contractorPagination = usePagination(10);
  const {
    items: contractors,
    currentPage: contractorPage,
    pageSize: contractorPageSize,
    totalItems: contractorTotalItems,
    setPaginationData: setContractorPaginationData,
    setCurrentPage: setContractorPage
  } = contractorPagination;

  const fetchContractors = useCallback(
    async (page) => {
      try {
        setLoading(true);
        const response = await api.get(`/contractors`, {
          params: {
            page,
            size: contractorPageSize,
            search: searchTerm.trim() || undefined,
            sortBy,
            direction,
            showDeleted
          }
        });
        setContractorPaginationData(response.data);
      } catch (error) {
        console.error("Error fetching contractors:", error);
        toast.error("Failed to load contractors");
      } finally {
        setLoading(false);
      }
    },
    [api, contractorPageSize, searchTerm, setContractorPaginationData, sortBy, direction, showDeleted]
  );

  useEffect(() => {
    fetchContractors(contractorPage);
  }, [contractorPage, fetchContractors]);

  const handleOpenContractorDialog = (contractor = null) => {
    setEditingContractor(contractor);
    setIsContractorDialogOpen(true);
  };

  const confirm = useConfirm();

  const handleDeleteContractor = async (id) => {
    const isConfirmed = await confirm({
      title: "Delete Contractor",
      message: "Are you sure you want to delete this contractor?"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/contractors/${id}`);
      toast.success("Contractor deleted");
      fetchContractors(contractorPage);
    } catch (error) {
      toast.error("Failed to delete contractor");
    }
  };

  // Permissions check
  const canRead = user?.permissions?.contractor?.read;

  if (!canRead && user?.role !== "super_admin") {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-yale-blue-700 mb-2">Access Denied</h2>
        <p className="text-yale-blue-500">You don't have permission to access this section.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 flex flex-col pb-8">
        <PageHeader
          title="Contractors"
          subtitle="Manage vendor contractors globally"
          pageIcon={HardHat}
          actionLabel={user?.permissions?.contractor?.create ? "Add Contractor" : null}
          onActionClick={() => handleOpenContractorDialog()}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput
                placeholder="Search contractors..."
                className="md:w-72"
                onSearch={(val) => {
                  setSearchTerm(val);
                  setContractorPage(0);
                }}
              />
            </div>
          }
        />

        <div className="flex-1 flex flex-col mt-4">
          <ContractorTable
            contractors={contractors}
            onEdit={handleOpenContractorDialog}
            onDelete={handleDeleteContractor}
            permissions={{
              canUpdate: user?.permissions?.contractor?.update,
              canDelete: user?.permissions?.contractor?.delete
            }}
            sortBy={sortBy}
            direction={direction}
            onSort={(key, dir) => {
              setSortBy(key);
              setDirection(dir);
              setContractorPage(0);
            }}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
            setContractorPage={setContractorPage}
          />
        </div>

        <div className="mt-auto pt-6">
          <Pagination pagination={contractorPagination} />
        </div>
      </div>

      <ContractorDialog
        isOpen={isContractorDialogOpen}
        onOpenChange={setIsContractorDialogOpen}
        editingContractor={editingContractor}
        onSuccess={() => fetchContractors(contractorPage)}
      />
    </div>
  );
};

export default Contractors;
